import type { SnapshotMeta } from '@/document/model'
import { computeCameraSnapshotForBounds } from '@/core/cameraFit'
import { defaultPaletteItems } from '@/components/palette/state'
import type { Position } from '@/voxelMath'
import type { CameraSnapshot } from '@/core/cameraFit'


const UNSET_SENTINEL = Symbol('SnapshotMeta unset value')

export const DEFAULT_BG = 0xf3f8fb

export function initialMeta(): SnapshotMeta {
  return {
    palette: defaultPaletteItems(),
    backgroundColor: DEFAULT_BG,
    bounds: UNSET_SENTINEL as unknown as Position,
    recentColors: [],
    lighting: {
      directional: {
        position: { x: 5, y: 15, z: -5 }
      },
    },
    camera: UNSET_SENTINEL as unknown as CameraSnapshot,
  }
}


export type HydrationMode = 'strict' | 'lenient'

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n)

function boundsAreValid(bounds: unknown): bounds is { x: number; y: number; z: number } {
  if (!bounds || typeof bounds !== 'object') return false
  const candidate = bounds as Partial<Position>
  return isFiniteNumber(candidate.x) && isFiniteNumber(candidate.y) && isFiniteNumber(candidate.z)
    && candidate.x > 0 && candidate.y > 0 && candidate.z > 0
}

// TODO we can use hydrateMeta before we compute base
export function hydrateMeta(meta: SnapshotMeta, mode: HydrationMode): SnapshotMeta {
  let next = { ...meta }

  if (!boundsAreValid(next.bounds)) {
    throw new Error('[hydrateSnapshotMeta] missing or invalid bounds')
  }

  const hasCamera = next.camera && typeof next.camera === 'object'
  const hasTarget = hasCamera && next.camera.target && isFiniteNumber(next.camera.target.x) && isFiniteNumber(next.camera.target.y) && isFiniteNumber(next.camera.target.z)
  if (!hasCamera || !hasTarget) {
    if (mode === 'strict') {
      throw new Error('[hydrateSnapshotMeta] missing camera target in strict mode')
    }
    const preferredMode = (next.camera?.mode === 'tilt' || next.camera?.mode === 'orbit')
      ? next.camera.mode
      : next.prefersCameraMode
    next.camera = computeCameraSnapshotForBounds(next.bounds, {
      target: {
        x: next.bounds.x / 2,
        y: next.bounds.y / 2,
        z: next.bounds.z / 2,
      },
      cameraMode: preferredMode,
    })
  }
  if (!meta.palette || meta.palette.length === 0) {
    if (mode === 'lenient') {
      // next.palette = inferPaletteFromBase(base)
      next.palette = defaultPaletteItems()
    } else {
      throw new Error('[hydrateSnapshotMeta] missing palette')
    }
  }

  if (!isFiniteNumber(meta.backgroundColor)) {
    if (mode === 'lenient') {
      next.backgroundColor = DEFAULT_BG
    } else {
      throw new Error('[hydrateSnapshotMeta] missing backgroundColor')
    }
  }

  if (!meta.lighting) {
    next.lighting = {
      directional: {
        position: { x: 5, y: 15, z: -5 }
      }
    }
  }

  if (!Array.isArray(meta.recentColors)) {
    next.recentColors = []
  }

  return next
}
